import {Button, TextArea, css, view} from '@sail/ui/deprecated';
import {useCallback, useEffect, useRef, useState} from 'react';
import {defineMessages, useIntl} from 'react-intl';

import {useTheme} from '@stripe-internal/conversations-ui';
import {CloseButton} from '../CloseButton';
import {useClientAnalytics} from '../../../Providers/ClientProvider/useClientAnalytics';
import {BrandedPrimaryButton} from '../BrandedPrimaryButton';
import {useApi} from '../../../Providers/ApiProvider/ApiProvider';

const COMMENT_MAX_LENGTH = 2000;

export const messages = defineMessages({
  formTitle: {
    id: 'support_conversations_client.supportWidget.feedback.title',
    description: 'Title shown in the form',
    defaultMessage: 'Share your experience',
  },
  submitButton: {
    id: 'support_conversations_client.supportWidget.feedback.submitButton',
    description: 'Submit button in feedback form',
    defaultMessage: 'Submit',
  },
  cancelButton: {
    id: 'support_conversations_client.supportWidget.feedback.cancelButton',
    description: 'Cancel button in the form',
    defaultMessage: 'Cancel',
  },
  commentPlaceholder: {
    id: 'support_conversations_client.supportWidget.feedback.comment.placeholder',
    description: 'Placeholder text in feedback form text area',
    defaultMessage: 'Add your comments',
  },
});

type Props = {
  conversationId: string;
  onDone: () => void;
  onCancel: () => void;
};

export function FeedbackForm({conversationId, onDone, onCancel}: Props) {
  const intl = useIntl();
  const theme = useTheme();
  const analytics = useClientAnalytics();
  const {submitFeedback} = useApi();

  const [submissionPending, setSubmissionPending] = useState(false);
  const [comment, setComment] = useState<string>('');
  const isFirstRenderRef = useRef<boolean>(false);

  const disabled = !comment || comment.length === 0;

  const handleCancel = useCallback(() => {
    analytics.action('support_conversations_feedback_cancel');
    onCancel();
  }, [analytics, onCancel]);

  const handleSubmit = useCallback(async () => {
    if (submissionPending) {
      return;
    }

    setSubmissionPending(true);
    analytics.action('support_conversations_feedback_submit');

    try {
      await submitFeedback({
        conversationId,
        comment,
      });
    } catch (e: any) {
      analytics.track('support_conversations_feedback_error');
    } finally {
      setSubmissionPending(false);
      onDone();
    }
  }, [
    submissionPending,
    analytics,
    submitFeedback,
    conversationId,
    comment,
    onDone,
  ]);

  useEffect(() => {
    if (!isFirstRenderRef.current) {
      isFirstRenderRef.current = true;
      analytics.track('support_conversations_feedback_impression');
    }
  }, [analytics]);

  return (
    <view.div
      data-testid="support-widget-feedback-form"
      css={{position: css.value('relative')}}
    >
      <view.form onSubmit={(e) => e.preventDefault()}>
        <view.h2
          css={{marginTop: 0, paddingTop: 'medium', marginRight: 'medium'}}
        >
          {intl.formatMessage(messages.formTitle)}
        </view.h2>

        <view.div>
          <TextArea
            label=""
            placeholder={intl.formatMessage(messages.commentPlaceholder)}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            required
            resizeable={false}
            maxLength={COMMENT_MAX_LENGTH}
            rows={8}
            id="comment"
            css={{
              '& textarea:focus': {
                outline: theme.focusVisibleOutline,
                focusRing: theme.focusVisibleOutline
                  ? css.value('none')
                  : undefined,
              },
            }}
          />
        </view.div>

        <view.div css={{marginTop: 'medium'}}>
          <BrandedPrimaryButton
            onPress={handleSubmit}
            disabled={disabled}
            pending={submissionPending}
            dataTestId="support-widget-feedback-submit-button"
          >
            {intl.formatMessage(messages.submitButton)}
          </BrandedPrimaryButton>
          <Button
            type="secondary"
            size="large"
            css={{width: 'fill', marginTop: 'small'}}
            onPress={handleCancel}
            data-testid="support-widget-feedback-cancel-button"
            disabled={false}
          >
            {intl.formatMessage(messages.cancelButton)}
          </Button>
        </view.div>

        <CloseButton
          onClick={handleCancel}
          dataTestId="support-widget-feedback-close-button"
        />
      </view.form>
    </view.div>
  );
}
